/**
 * LocalInventoryMatches — dealer-only panel shown inside the Lead Detail
 * modal. Ranks the dealer's uploaded inventory against the lead's match
 * criteria and shows up to 3 top hits. These are NOT sent to the buyer;
 * they exist so the dealer can quickly see which local units fit before
 * following up.
 *
 * Silent if the dealer has never uploaded any inventory files (removes
 * clutter for dealers who don't use this feature).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Package, CheckCircle2, DollarSign, Ruler, Users } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n == null) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
  } catch { return `$${n}`; }
};

const LocalInventoryMatches = ({ leadId, isDark = true }) => {
  const [state, setState] = useState({ loading: true, matches: [], inventory_size: 0 });

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    (async () => {
      const token = getAuthToken();
      try {
        const res = await axios.get(`${API}/dealer/lead/${leadId}/local-matches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setState({ loading: false, ...res.data });
      } catch {
        if (!cancelled) setState({ loading: false, matches: [], inventory_size: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  // Don't render anything if this dealer has no uploaded inventory yet —
  // avoids empty-state clutter on brand-new accounts.
  if (state.loading) {
    return (
      <div className="mt-6 flex items-center gap-2 text-xs text-slate-400" data-testid="local-matches-loading">
        <Loader2 size={12} className="animate-spin" /> Checking your uploaded inventory…
      </div>
    );
  }
  if (state.inventory_size === 0) return null;

  return (
    <div className="mt-6 space-y-3" data-testid="local-inventory-matches">
      <div className="flex items-center justify-between gap-2">
        <h3 className={`text-sm font-bold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}>
          <Package size={16} />
          From Your Inventory ({state.matches.length})
        </h3>
        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${isDark ? 'bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/30' : 'bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200'}`}>
          Dealer eyes only
        </span>
      </div>

      {state.matches.length === 0 ? (
        <div
          className={`rounded-xl border-2 border-dashed p-5 text-center text-xs ${isDark ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'}`}
          data-testid="local-matches-none"
        >
          None of your {state.inventory_size} uploaded RVs match this lead's criteria. Consider adjusting stock or uploading new inventory.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {state.matches.map((m) => (
            <div
              key={m.id}
              className={`rounded-xl border p-4 flex flex-col gap-2 ${isDark
                ? 'border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/[0.08] to-violet-500/[0.03]'
                : 'border-fuchsia-200 bg-fuchsia-50/50'}`}
              data-testid={`local-match-${m.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-fuchsia-300' : 'text-fuchsia-700'}`}>
                    {m.class || 'RV'}{m.condition ? ` · ${m.condition}` : ''}
                  </p>
                  <p className={`text-sm font-black leading-tight mt-0.5 truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {[m.year, m.make, m.model].filter(Boolean).join(' ')}
                  </p>
                </div>
                <span className={`shrink-0 text-[10px] font-black px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-200' : 'bg-emerald-100 text-emerald-800'}`}>
                  {m.match_score} pts
                </span>
              </div>

              <div className={`grid grid-cols-3 gap-2 text-[11px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {m.price != null && (
                  <div className="flex items-center gap-1"><DollarSign size={11} className="opacity-60" />{fmtMoney(m.price)}</div>
                )}
                {m.length_ft != null && (
                  <div className="flex items-center gap-1"><Ruler size={11} className="opacity-60" />{m.length_ft}ft</div>
                )}
                {m.sleeps != null && (
                  <div className="flex items-center gap-1"><Users size={11} className="opacity-60" />Sleeps {m.sleeps}</div>
                )}
              </div>

              {m.stock_number && (
                <p className={`text-[10px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Stock #{m.stock_number}</p>
              )}

              {m.match_reasons?.length > 0 && (
                <ul className="space-y-1 pt-1 border-t border-white/10">
                  {m.match_reasons.slice(0, 3).map((r, i) => (
                    <li key={i} className={`text-[11px] flex items-start gap-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalInventoryMatches;
